import { keyframes } from '@emotion/react';
import { chipClasses, filledInputClasses, formHelperTextClasses, formLabelClasses, inputAdornmentClasses, inputBaseClasses, inputClasses, inputLabelClasses, TextField as MuiTextField, outlinedInputClasses, selectClasses } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { forwardRef, memo } from 'react';
import { useERACustomizer } from 'src/contexts/era-package/EraCustomizerContext';

/**
 * ERA² TextField component that provides a unified input experience across two design systems.
 *
 * This component serves as the foundation for all text input fields in the application,
 * supporting both the standard MUI design system and the ERA² design system via the 'alt' prop.
 *
 * Key Features:
 * - Dual design system support (standard vs ERA² via 'alt' prop)
 * - Animated required field indicators with custom asterisk
 * - Consistent styling across outlined, filled, and standard variants
 * - Advanced input adornment positioning
 * - Redux integration for global theme customization
 * - Enhanced accessibility and visual feedback
 *
 * @example
 * // Standard usage
 * <TextField
 *   label="Email Address"
 *   type="email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   required
 * />
 *
 * @example
 * // ERA² design system mode
 * <TextField
 *   label="User Name"
 *   value={userName}
 *   onChange={handleChange}
 *   alt={true}
 *   variant="filled"
 * />
 *
 * @example
 * // With input adornments
 * <TextField
 *   label="Price"
 *   type="number"
 *   startAdornment={<InputAdornment position="start">$</InputAdornment>}
 *   endAdornment={<InputAdornment position="end">USD</InputAdornment>}
 * />
 */

// ANIMATION KEYFRAMES: Custom asterisk animations for required field indicators
// These provide smooth visual feedback when fields become required or filled

// Entrance animation: Asterisk appears with rotation and scale effect
const asteriskIn = keyframes`  
  0% {
    opacity: 0;
    transform: rotate(180deg) scale(2);    
  }  
  100% {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }
`;

// Exit animation: Asterisk disappears with scale and fade effect
const asteriskOut = keyframes`  
  0% {
    opacity: 1;
    transform: scale(1);    
  }
  67% {
    opacity: 0.25;
    transform: scale(1.5);
  }    
  100% {
    opacity: 0;    
    transform: scale(2);
  }
`;

/**
 * Styled TextField wrapper that handles complex styling across variants and design systems.
 *
 * The styling logic is organized by:
 * 1. Filled variant customizations
 * 2. Standard variant customizations
 * 3. Outlined variant customizations
 * 4. Helper text positioning
 * 5. Label behavior and animations
 * 6. Input adornment positioning
 */

const StyledTextField = styled(MuiTextField)(({ theme, color, alt, size, borderradius }) => ({
  // FILLED VARIANT: Custom hover effects
  // Provides subtle background color change on hover using theme color at low opacity
  [`:hover > .${inputBaseClasses.root}.${filledInputClasses.root}:not(.Mui-disabled)`]: {
    backgroundColor: alpha(theme.palette[color].main, 0.05),
  },

  [`> .${inputLabelClasses.root}`]: {
    color: 'red'
  }, // ??????????????? reset???

  // STANDARD VARIANT: Enhanced underline styling and input adornment positioning
  [`> .${inputBaseClasses.root}`]: {
    // Ensure disabled fields maintain transparent background
    '&.Mui-disabled': {
      backgroundColor: 'transparent',
    },

    // CUSTOM UNDERLINE: Replace default MUI underline with custom border styling
    '&:after': {
      borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.5)}`,
      transform: 'unset', // Remove default transform animations
      transition: theme.transitions.create('borderColor'),
    },

    // ERROR STATE: Apply error color to underline
    '&.Mui-error:after': {
      borderColor: theme.palette.error.main,
    },

    // FOCUS STATE: Enhanced focus styling with theme color
    '&.Mui-focused': {
      '&:after': {
        borderWidth: 2,
        borderColor: `${theme.palette[color].main}!important`,
      },
      // Ensure select components don't interfere with focus styles
      [`> .${selectClasses.standard}`]: {
        backgroundColor: 'unset',
      },
    },

    // INPUT ADORNMENT POSITIONING: Precise alignment for start/end adornments
    [`> .${inputAdornmentClasses.positionStart}`]: {
      height: '100%',
      maxHeight: 'none',
      [`&.${inputAdornmentClasses.sizeSmall}`]: {
        paddingBottom: '5px',
      },
      [`&.${inputAdornmentClasses.sizeMedium}`]: {
        paddingBottom: alt ? '1px' : '3px', // Different padding based on design system
      },
    },
    [`> .${inputAdornmentClasses.positionEnd}`]: {
      height: '100%',
      maxHeight: 'none',
      [`&.${inputAdornmentClasses.sizeSmall}`]: {
        paddingBottom: '5px',
      },
      [`&.${inputAdornmentClasses.sizeMedium}`]: {
        paddingBottom: alt ? '1px' : '3px', // ERA² vs standard padding
      },
    },
  },

  // HOVER EFFECTS: Enhanced hover state for standard variant
  [`&:hover > .${inputClasses.root}:not(.Mui-focused):after`]: {
    borderColor: theme.palette.text.primary,
  },

  // ERA² DESIGN SYSTEM: Label positioning adjustments for alternative design
  [`> .${inputLabelClasses.root} + .${inputClasses.root}`]: {
    marginTop: alt ? '1rem' : 0, // Extra margin for ERA² floating labels
  },

  // OUTLINED VARIANT: Border radius, focus states, and adornment positioning
  [`> .${outlinedInputClasses.root}`]: {
    // DYNAMIC BORDER RADIUS: Use customizer value from Redux store
    borderRadius: borderradius + 'px',

    // ERROR STATE: Apply error styling to border
    [`&.Mui-error > .${outlinedInputClasses.notchedOutline}`]: {
      borderColor: theme.palette.error.main,
    },

    // FOCUS STATE: Enhanced focus border with theme color
    [`&.Mui-focused > .${outlinedInputClasses.notchedOutline}`]: {
      borderWidth: 2,
      borderColor: `${theme.palette[color].main}!important`,
    },

    // INPUT ADORNMENT POSITIONING: Precise alignment and padding adjustments
    [`> .${inputAdornmentClasses.positionStart}`]: {
      height: '100%',
      maxHeight: 'none',
      [`&.${inputAdornmentClasses.sizeSmall}`]: {
        paddingBottom: '1px',
      },
      [`&.${inputAdornmentClasses.sizeMedium}`]: {
        paddingBottom: alt ? '1px' : 0, // ERA² vs standard alignment
      },
      // PADDING OPTIMIZATION: Remove left padding when start adornment is present
      [`+ .${outlinedInputClasses.input}`]: {
        paddingLeft: 0,
      },
    },
    // PADDING OPTIMIZATION: Remove right padding when end adornment is present
    [`> .${outlinedInputClasses.input}:has(+ .${inputAdornmentClasses.positionEnd})`]: {
      paddingRight: 0,
    },
    [`> .${inputAdornmentClasses.positionEnd}`]: {
      height: '100%',
      maxHeight: 'none',
      [`&.${inputAdornmentClasses.sizeSmall}`]: {
        paddingBottom: '1px',
      },
      [`&.${inputAdornmentClasses.sizeMedium}`]: {
        paddingBottom: alt ? '1px' : 0, // Consistent with start adornment
      },
    },
  },

  // HOVER EFFECTS: Enhanced hover state for outlined variant
  [`&:hover > .${outlinedInputClasses.root}:not(.Mui-focused) > .${outlinedInputClasses.notchedOutline}`]: {
    borderColor: theme.palette.text.primary,
  },

  // FILLED VARIANT: Background colors, border removal, and adornment positioning
  [`> .${filledInputClasses.root}`]: {
    // CONSISTENT STYLING: Match border radius with other variants
    borderRadius: borderradius + 'px',
    // SUBTLE BACKGROUND: Light tint using theme color
    backgroundColor: alpha(theme.palette[color].main, 0.03),

    // DISABLED STATE: Use theme's disabled background
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
    },

    // BORDER REMOVAL: Hide default MUI filled variant borders
    '&:before': {
      display: 'none', // Remove bottom border
    },
    '&:after': {
      display: 'none', // Remove underline effect
    },

    // FOCUS AND SELECT STATES: Maintain consistent background color
    [`&.Mui-focused, &.${selectClasses.select}`]: {
      backgroundColor: alpha(theme.palette[color].main, 0.03),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette[color].main, 0.03),
      // Ensure select components don't interfere
      [`> .${selectClasses.filled}`]: {
        backgroundColor: 'unset',
      },
    },

    // INPUT ADORNMENT POSITIONING: Complex positioning for filled variant
    [`> .${inputAdornmentClasses.positionStart}`]: {
      height: '100%',
      maxHeight: 'none',
      marginTop: '0!important', // Override default margins
      paddingTop: alt ? '16px' : 0, // ERA² requires top padding
      [`&.${inputAdornmentClasses.sizeSmall}`]: {
        paddingBottom: '1px',
      },
    },
    [`> .${inputAdornmentClasses.positionEnd}`]: {
      height: '100%',
      maxHeight: 'none',
      paddingTop: alt ? '16px' : 0, // Match start adornment padding
    },

    // ERA² INPUT PADDING: Complex padding logic based on design system and size
    [`> .${filledInputClasses.input}`]: {
      // ERA² mode requires different padding for proper label clearance
      paddingTop: alt ? (size === 'medium' ? '25px' : '21px') : size === 'medium' ? '12px' : '8px',
      paddingBottom: alt ? (size === 'medium' ? '8px' : '4px') : size === 'medium' ? '12px' : '8px',
    },
  },

  // HELPER TEXT POSITIONING: Align helper text with input field content

  // OUTLINED VARIANT: Left-align helper text with input content
  [`> .${outlinedInputClasses.root} + .${formHelperTextClasses.root}`]: {
    marginLeft: alt ? '14px' : 0, // Match outlined input padding
  },

  // FILLED VARIANT: Left-align helper text with input content
  [`> .${filledInputClasses.root} + .${formHelperTextClasses.root}`]: {
    marginLeft: alt ? '12px' : 0, // Match filled input padding
  },

  // OUTLINED VARIANT NOTCH: Control label notch visibility based on design system
  // NOTE: not working; not applying; copied to theme component overrides
  [`> .${outlinedInputClasses.root} > .${outlinedInputClasses.notchedOutline} > legend > span`]: {
    display: alt ? 'block' : 'none', // ERA² shows notch, standard hides it
  },

  // TEXTAREA: Reduce the excessive padding used in TextArea
  [`> .${inputBaseClasses.multiline}`]: {
    [`&.${filledInputClasses.root}`]: {
      padding: '1rem',
    },
    [`> .${inputBaseClasses.inputMultiline}`]: {
      padding: '0',
    },
  },

  // FLOATING LABEL SYSTEM: Complex label positioning and animation logic
  [`> .${inputLabelClasses.root}`]: {
    transition: theme.transitions.create('all'),

    // BASE LABEL STYLING: Different positioning based on design system
    position: alt ? 'absolute' : 'relative', // ERA² uses absolute positioning
    marginBottom: alt ? 0 : '5px', // Standard mode adds bottom margin
    fontSize: alt ? theme.typography.body1.fontSize : theme.typography.subtitle1.fontSize,
    fontWeight: alt ? theme.typography.body1.fontWeight : 500,
    transform: alt ? '' : 'none!important', // Disable transform for standard mode

    // SHRINK BEHAVIOR: Control label shrinking animation
    [`&.${inputLabelClasses.shrink}`]: {
      transform: alt ? '' : 'none!important', // Only ERA² uses transform animations
    },

    // OUTLINED VARIANT LABEL: Precise positioning for floating labels
    [`&.${inputLabelClasses.outlined}.${inputLabelClasses.sizeMedium}`]: {
      transform: 'translate(14px, 13px) scale(1)', // Default position

      // FLOATING STATE: Label position when floated above input
      [`&.${inputLabelClasses.shrink}`]: {
        transform: 'translate(14px, -9px) scale(0.75)', // Shrunk position
      },
    },

    // REQUIRED FIELD STYLING: Custom animated asterisk system
    '&.Mui-required': {
      paddingLeft: '13px', // Space for custom asterisk

      // HIDE DEFAULT: Remove MUI's default asterisk
      [`> .${inputLabelClasses.asterisk}`]: {
        display: 'none',
      },

      // CUSTOM ASTERISK: Animated heavy asterisk with theme color
      '&::before': {
        position: 'absolute',
        left: '-2px',
        display: 'flex',
        content: '"\\2731"', // Unicode heavy asterisk '✱' - better visual centering than '*'
        color: theme.palette[color].main,
        opacity: '0',
        animation: `${asteriskIn} 0.5s ease-out forwards`, // Entrance animation
        transition: theme.transitions.create('transform'),
      },

      // HIDE WHEN FILLED: Remove asterisk when field has content
      [`&.${formLabelClasses.filled}`]: {
        paddingLeft: 0,
        '&::before': {
          opacity: '1',
          animation: `${asteriskOut} 0.5s ease forwards`, // Exit animation
        },
      },

      // AUTOCOMPLETE DETECTION: Hide asterisk when chips are present (multi-select)
      [`&:has(+.${inputBaseClasses.root} > .${chipClasses.root})`]: {
        paddingLeft: 0,
        '&::before': {
          opacity: '1',
          animation: `${asteriskOut} 0.5s ease forwards`,
        },
      },
    },

    // DISABLED FIELD STYLING: Consistent disabled appearance across all elements
    '&.Mui-disabled:not(.Mui-error)': {
      color: theme.palette.text.disabled, // Disabled label color

      // DISABLED INPUT: Apply disabled styling to input text and borders
      [`~ .${inputBaseClasses.root}`]: {
        [`> .${inputBaseClasses.input}`]: {
          color: theme.palette.text.disabled, // Disabled input text
        },
        [`> .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.palette.text.disabled, // Disabled border
        },
      },
      // DISABLED HELPER TEXT: Match helper text color
      [`~ .${formHelperTextClasses.root}`]: {
        color: theme.palette.text.disabled,
      },
    },

    // LABEL LENGTH OPTIMIZATION: Prevent label overflow with end adornments
    [`&:not(.${inputLabelClasses.shrink}):has( + .${inputBaseClasses.root} > .${inputAdornmentClasses.positionEnd})`]: {
      paddingRight: alt ? '2rem' : 0, // Add padding to prevent overlap with end adornment
    },
  },
}));

/**
 * TextField component that bridges MUI TextField with custom styling and Redux integration.
 *
 * This component automatically:
 * - Merges user-provided InputProps with internal adornment props
 * - Applies border radius from Redux customizer state
 * - Handles label shrinking behavior based on design system mode
 * - Provides consistent prop forwarding to underlying MUI component
 */
const TextField = forwardRef((props, ref) => {
  const {
    // STYLING PROPS: Visual appearance and behavior
    color = 'primary', // Theme color for focus states and accents
    variant = 'outlined', // Input style: outlined | filled | standard
    alt = false, // ERA² design system mode toggle
    size = 'medium', // Input size: small | medium

    // INPUT ADORNMENTS: Icons or text at start/end of input
    startAdornment, // Content at the beginning of input
    endAdornment, // Content at the end of input

    // CUSTOMIZATION PROPS: Advanced styling overrides
    InputProps: userInputProps = {}, // Props for the Input component
    InputLabelProps: userInputLabelProps = {}, // Props for the InputLabel component

    ...otherProps // All other TextField props (value, onChange, etc.)
  } = props;

  // REDUX INTEGRATION: Get global customizer settings for border radius
  const customizer = useERACustomizer()

  // PROPS MERGING: Combine user props with internal adornment handling
  const mergedInputProps = {
    ...userInputProps,
    // CONDITIONAL ADORNMENTS: Only add if provided to prevent empty prop objects
    ...(startAdornment && { startAdornment }),
    ...(endAdornment && { endAdornment }),
  };

  // LABEL BEHAVIOR: Handle label shrinking based on design system
  const mergedInputLabelProps = {
    ...userInputLabelProps,
    // STANDARD MODE: Always shrink labels (no floating behavior)
    // ERA² MODE: Allow default MUI floating behavior
    ...(!alt && { shrink: true }),
  };

  return (
    <StyledTextField
      color={color}
      variant={variant}
      alt={alt ? 'true' : ''} // Convert boolean to string for styled-components
      size={size}
      borderradius={customizer.borderRadius} // Dynamic border radius from Redux
      InputProps={mergedInputProps}
      InputLabelProps={mergedInputLabelProps}
      {...otherProps} // Forward all other props (value, onChange, label, etc.)
      ref={ref}
    />
  );
});

// COMPONENT METADATA: For debugging and development tools
TextField.displayName = 'TextField';

// COMPREHENSIVE PROP VALIDATION: Runtime type checking and documentation
TextField.propTypes = {
  // CORE STYLING PROPS
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
  ]) /** Theme color for focus states and accents */,
  variant: PropTypes.oneOf(['', 'outlined', 'filled', 'standard']) /** Input style variant */,
  alt: PropTypes.bool /** Enable ERA² design system mode (affects label positioning, padding, and animations) */,
  size: PropTypes.oneOf(['small', 'medium']) /** Input field size */,
  borderRadius: PropTypes.number /** Border radius in pixels (typically from Redux customizer) */,

  // CONTENT PROPS
  label: PropTypes.string /** Field label text */,
  placeholder: PropTypes.string /** Placeholder text when field is empty */,
  value: PropTypes.any /** Controlled component value */,
  defaultValue: PropTypes.any /** Default value for uncontrolled usage */,
  helperText: PropTypes.string /** Helper text displayed below the field */,

  // STATE PROPS
  required: PropTypes.bool /** Mark field as required (shows animated asterisk) */,
  disabled: PropTypes.bool /** Disable all interactions */,
  error: PropTypes.bool /** Apply error styling */,
  select: PropTypes.bool /** Enable select/dropdown mode */,
  children: PropTypes.node /** Child elements (typically for select options) */,

  // ADVANCED CUSTOMIZATION PROPS
  hiddenLabel:
    PropTypes.bool /** Hide the label completely. Note: Will not work properly if 'label' prop is provided. */,
  startAdornment:
    PropTypes.node /** Content displayed at the start of the input (icons, text, etc.) */,
  endAdornment:
    PropTypes.node /** Content displayed at the end of the input (icons, buttons, etc.) */,
  InputProps: PropTypes.object /** Props passed to the underlying Input component */,
  InputLabelProps: PropTypes.object /** Props passed to the InputLabel component */,
  SelectProps: PropTypes.object /** Props passed to Select component when select=true */,
  onChange: PropTypes.func /** Callback fired when input value changes */,
};

export default memo(TextField);

// Display name for React DevTools debugging
TextField.displayName = 'ERA² TextField';

// REACT VERSION COMPATIBILITY NOTES:
// ==================================
//
// CURRENT APPROACH (React 18 Style):
// - Uses React.forwardRef for explicit ref forwarding
// - Compatible with React 16.3+ through React 18
// - Clear, debuggable ref handling
// - Consistent with existing codebase patterns
//
// FUTURE MIGRATION (React 19):
// When migrating to React 19, this component can be simplified to:
//
// const DebouncedTextField = ({ ref, maxLength = 0, debounceDelay = 500, ... }) => {
//   // Same implementation, but ref is just another prop
//   return <TextField ref={ref} ... />;
// };
//
// MIGRATION CHECKLIST:
// 1. Ensure all consuming components work with React 19
// 2. Remove React.forwardRef wrapper
// 3. Add ref to destructured props
// 4. Update PropTypes to include ref
// 5. Test ref forwarding functionality
// 6. Update documentation

// USAGE GUIDELINES:
// =================
//
// 1. DESIGN SYSTEM MODES:
//    - alt={false}: Standard mode with static labels and consistent spacing
//    - alt={true}: ERA² mode with floating labels and alternative padding
//
// 2. REQUIRED FIELD BEHAVIOR:
//    - Animated asterisk appears when required={true}
//    - Asterisk hides automatically when field has content
//    - Uses heavy asterisk (✱) for better visual balance
//
// 3. INPUT ADORNMENTS:
//    - Use startAdornment/endAdornment for icons or additional content
//    - Component automatically adjusts padding to accommodate adornments
//    - Adornments are properly aligned across all variants and sizes
//
// 4. PERFORMANCE CONSIDERATIONS:
//    - Component uses Redux selector for borderRadius (consider memoization for heavy forms)
//    - Complex CSS-in-JS styling may impact render performance with many instances
//    - Consider wrapping in React.memo for static forms
//
// 5. ACCESSIBILITY:
//    - All ARIA attributes are passed through to underlying MUI component
//    - Required fields are properly marked for screen readers
//    - Error states provide appropriate color contrast
//    - Label associations are maintained across all variants
